import {
    type KeyboardEvent,
    type ReactNode,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import {
    type DataGridDraftRowOptions,
    DraftRowContext,
} from "../features/draftRows";
import { useCellControlTabIndex } from "./cellControls";
import styles from "./DraftRowInput.module.scss";

interface DraftRowInputProps<TData> {
    row: TData;
    options: DataGridDraftRowOptions<TData>;
}

/**
 * A draft row's create input. Enter or leaving it with text creates and
 * clears it for the next draft; Escape, Enter on blank text, or leaving it
 * blank cancels, which settles the draft for good. Its keys never reach the
 * grid.
 */
export const DraftRowInput = <TData,>({
    row,
    options,
}: Readonly<DraftRowInputProps<TData>>) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const hasSettledRef = useRef(false);
    const [value, setValue] = useState("");
    const tabIndex = useCellControlTabIndex();
    const shouldAutoFocus = options.shouldAutoFocus?.(row) ?? false;

    useEffect(() => {
        if (shouldAutoFocus) {
            inputRef.current?.focus();
        }
    }, [shouldAutoFocus]);

    const cancel = () => {
        hasSettledRef.current = true;
        options.onCancel(row);
    };

    /** Settled only while `onCreate` runs, so a focus move inside it can't create twice. */
    const create = (trimmed: string) => {
        hasSettledRef.current = true;
        try {
            options.onCreate({ row, value: trimmed });
            setValue("");
        } finally {
            hasSettledRef.current = false;
        }
    };

    const settle = () => {
        const trimmed = value.trim();
        if (trimmed) {
            create(trimmed);
        } else {
            cancel();
        }
    };

    const handleBlur = () => {
        if (!hasSettledRef.current) {
            settle();
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (
            event.nativeEvent.isComposing ||
            (event.key !== "Enter" && event.key !== "Escape")
        ) {
            return;
        }
        event.preventDefault();
        if (hasSettledRef.current) {
            return;
        }
        if (event.key === "Enter") {
            settle();
        } else {
            cancel();
        }
    };

    return (
        <span className={styles.controls} data-dt-controls="">
            <input
                ref={inputRef}
                className={styles.input}
                data-dt-editor=""
                aria-label={options.placeholder}
                placeholder={options.placeholder}
                tabIndex={tabIndex}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                onFocus={(event) => event.currentTarget.select()}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
            />
        </span>
    );
};

interface DraftInputSlotProps {
    columnId: string;
    children: ReactNode;
}

/** The create input in a draft row's draft column; the cell's content elsewhere. */
export const DraftInputSlot = ({
    columnId,
    children,
}: Readonly<DraftInputSlotProps>) => {
    const draftCell = useContext(DraftRowContext);
    return <>{draftCell?.columnId === columnId ? draftCell.input : children}</>;
};

interface DraftRowCellsProps<TData> extends DraftRowInputProps<TData> {
    children: ReactNode;
}

/** A draft row's cells; the draft column renders the create input. */
export const DraftRowCells = <TData,>({
    row,
    options,
    children,
}: Readonly<DraftRowCellsProps<TData>>) => {
    const draftCell = {
        columnId: options.columnId,
        input: <DraftRowInput row={row} options={options} />,
    };
    return (
        <DraftRowContext.Provider value={draftCell}>
            {children}
        </DraftRowContext.Provider>
    );
};
